import { createFileRoute } from "@tanstack/react-router";
import { ogImageMeta } from "@/lib/seo/og-image";
import { LocaleLink } from "@/components/site/LocaleLink";
import { Fragment, useEffect, useState, type ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  Download,
  Radio,
  BarChart3,
  ShieldAlert,
  Eye,
  Wand2,
  Users,
  Globe2,
  Building2,
  Landmark,
  Pill,
  ShoppingBag,
  Store,
  Car,
  HeartHandshake,
  Megaphone,
  Briefcase,
  Newspaper,
  Mail,
  FileText,
  LayoutDashboard,
  Bell,
  Code2,
  Cpu,
  UserCheck,
  Map,
  Layers,
  Filter,
  Activity,
  TrendingUp,
  Target,
  AlertTriangle,
  Lightbulb,
  CheckCircle2,
  Database,
  Network,
  Tag,
  Search,
  Workflow,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/site/Reveal";

export const Route = createFileRoute("/company-presentation")({
  head: () => ({
    meta: [
      { title: "Company Presentation | Perceptica" },
      { name: "robots", content: "noindex,nofollow" },
      {
        name: "description",
        content:
          "Perceptica - From Monitoring to Intelligence. Interactive sales presentation.",
      },
      { property: "og:title", content: "Company Presentation | Perceptica" },
      { property: "og:description", content: "Perceptica - From Monitoring to Intelligence. Interactive sales presentation." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://perceptica.com/company-presentation" },
      ...ogImageMeta("en", "/company-presentation", "Perceptica company presentation"),
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: CompanyPresentation,
});

/* ---------------------------------------------------------------- */
/* Local atoms reusing the site's design tokens                      */
/* ---------------------------------------------------------------- */

function Kicker({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-background/60 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground backdrop-blur">
      <span className="h-1.5 w-1.5 rounded-full bg-accent" />
      {children}
    </span>
  );
}

function SectionShell({
  id,
  tone = "light",
  children,
  className = "",
}: {
  id: string;
  tone?: "light" | "soft" | "dark";
  children: ReactNode;
  className?: string;
}) {
  const bg =
    tone === "dark"
      ? "bg-primary text-white"
      : tone === "soft"
        ? "bg-[oklch(0.985_0.005_270)]"
        : "bg-background";
  return (
    <section
      id={id}
      className={`relative overflow-hidden ${bg} py-24 lg:py-32 ${className}`}
    >
      {tone === "dark" && (
        <>
          <div className="pointer-events-none absolute inset-0 bg-radial-hero opacity-80" />
          <div className="grid-overlay" />
        </>
      )}
      <div className="relative mx-auto max-w-[1280px] px-6 lg:px-10">{children}</div>
    </section>
  );
}

function SectionHeader({
  kicker,
  title,
  body,
  align = "center",
  invert = false,
}: {
  kicker?: string;
  title: ReactNode;
  body?: ReactNode;
  align?: "center" | "left";
  invert?: boolean;
}) {
  return (
    <div
      className={`mb-14 ${align === "center" ? "mx-auto max-w-3xl text-center" : "max-w-3xl"}`}
    >
      {kicker && (
        <div className={`mb-5 flex ${align === "center" ? "justify-center" : ""}`}>
          <Kicker>{kicker}</Kicker>
        </div>
      )}
      <h2
        className={`font-display text-[clamp(2rem,4vw,3.25rem)] font-medium leading-[1.05] tracking-[-0.025em] ${
          invert ? "text-white" : "text-foreground"
        }`}
      >
        {title}
      </h2>
      {body && (
        <p
          className={`mt-6 text-[clamp(1.05rem,1.3vw,1.2rem)] leading-relaxed ${
            invert ? "text-white/75" : "text-muted-foreground"
          }`}
        >
          {body}
        </p>
      )}
    </div>
  );
}

function FeatureCard({
  icon: Icon,
  title,
  body,
  className = "",
}: {
  icon: typeof Radio;
  title: string;
  body: string;
  className?: string;
}) {
  return (
    <div
      className={`group relative rounded-2xl border border-border/70 bg-background p-6 shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-card ${className}`}
    >
      <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-brand text-white shadow-pill">
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="font-display text-lg font-semibold text-foreground">{title}</h3>
      <p className="mt-2 text-[14.5px] leading-relaxed text-muted-foreground">{body}</p>
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* Sticky in-page nav                                                */
/* ---------------------------------------------------------------- */

const NAV = [
  { id: "hero", label: "Intro" },
  { id: "challenge", label: "Challenge" },
  { id: "shift", label: "The Shift" },
  { id: "meet", label: "Perceptica" },
  { id: "what", label: "What We Do" },
  { id: "flow", label: "Intelligence Flow" },
  { id: "platform", label: "Platform" },
  { id: "ai-human", label: "AI + Human" },
  { id: "see", label: "SEE Advantage" },
  { id: "use-cases", label: "Use Cases" },
  { id: "industries", label: "Industries" },
  { id: "delivery", label: "Delivery" },
  { id: "why", label: "Why Us" },
  { id: "trusted", label: "Trusted" },
  { id: "cta", label: "Get Started" },
];

function PresoNav({ onDownload }: { onDownload: () => void }) {
  const [active, setActive] = useState("hero");
  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    NAV.forEach((n) => {
      const el = document.getElementById(n.id);
      if (!el) return;
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) setActive(n.id);
          });
        },
        { rootMargin: "-40% 0px -55% 0px", threshold: 0 },
      );
      io.observe(el);
      observers.push(io);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, []);

  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-primary/85 backdrop-blur-xl print:hidden">
      <div className="mx-auto flex h-14 max-w-[1400px] items-center gap-6 px-6 lg:px-10">
        <LocaleLink to="/" className="font-display text-[15px] font-semibold tracking-tight text-white">
          Perceptica<span className="ml-2 text-white/70">/ Presentation</span>
        </LocaleLink>
        <nav className="hidden flex-1 items-center gap-1 overflow-x-auto lg:flex">
          {NAV.map((n) => (
            <a
              key={n.id}
              href={`#${n.id}`}
              className={`whitespace-nowrap rounded-full px-3 py-1.5 text-[12px] font-medium transition-colors ${
                active === n.id
                  ? "bg-white text-primary"
                  : "text-white/65 hover:bg-white/10 hover:text-white"
              }`}
            >
              {n.label}
            </a>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-2">
          <Button
            size="sm"
            variant="outlineLight"
            onClick={onDownload}
            className="hidden md:inline-flex"
          >
            <Download className="mr-1.5 h-4 w-4" /> PDF
          </Button>
          <Button size="sm" asChild>
            <LocaleLink to="/request-a-demo">Request a Demo</LocaleLink>
          </Button>
        </div>
      </div>
    </header>
  );
}

/* ---------------------------------------------------------------- */
/* Page                                                              */
/* ---------------------------------------------------------------- */

function CompanyPresentation() {
  const handleDownload = () => {
    if (typeof window !== "undefined") window.print();
  };

  return (
    <div className="bg-background">
      <PresoNav onDownload={handleDownload} />
      <main>
        <Hero onDownload={handleDownload} />
        <Challenge />
        <Shift />
        <Meet />
        <WhatWeDo />
        <Flow />
        <Platform />
        <AiHuman />
        <SEE />
        <UseCases />
        <Industries />
        <Delivery />
        <Why />
        <Trusted />
        <FinalCta />
      </main>
      <PrintStyles />
    </div>
  );
}

/* ---------------- Print stylesheet ---------------- */
function PrintStyles() {
  return (
    <style>{`
        @media print {
          @page { size: A4 landscape; margin: 12mm; }
        html, body { background: #fff !important; }
        header.sticky, .print\\:hidden { display: none !important; }
        section { page-break-inside: avoid; padding-top: 24px !important; padding-bottom: 24px !important; }
        .bg-primary { background: #0D0F2B !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
      }
    `}</style>
  );
}

/* ============================ SECTION 1: HERO ============================ */
function Hero({ onDownload }: { onDownload: () => void }) {
  return (
    <section
      id="hero"
      className="relative overflow-hidden bg-primary pb-28 pt-20 text-white lg:pb-36 lg:pt-28"
    >
      <div className="pointer-events-none absolute inset-0 bg-radial-hero opacity-90" />
      <div className="grid-overlay" />
      <div className="relative mx-auto max-w-[1280px] px-6 lg:px-10">
        <div className="grid items-center gap-14 lg:grid-cols-[1.05fr_1fr]">
          <Reveal>
            <Kicker>Perceptica · Corporate Presentation</Kicker>
            <h1 className="mt-6 font-display text-[clamp(2.75rem,6vw,5.25rem)] font-medium leading-[0.98] tracking-[-0.035em]">
              Perceptica
              <span className="mt-3 block text-gradient-brand">
                From Monitoring to Intelligence
              </span>
            </h1>
            <p className="mt-7 max-w-xl text-[clamp(1.05rem,1.3vw,1.2rem)] leading-relaxed text-white/75">
              Media intelligence for organisations that need to understand reputation,
              narratives, risks and public perception across Southeast Europe.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Button size="lg" asChild>
                <LocaleLink to="/request-a-demo">
                  Request a Demo <ArrowRight className="h-4 w-4" />
                </LocaleLink>
              </Button>
              <Button size="lg" variant="outlineLight" asChild>
                <a href="#what">Explore Capabilities</a>
              </Button>
              <button
                onClick={onDownload}
                className="ml-1 inline-flex items-center gap-2 text-[13px] font-semibold text-white/80 underline-offset-4 hover:text-white hover:underline"
              >
                <Download className="h-4 w-4" /> Download PDF
              </button>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <HeroVisual />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function HeroVisual() {
  const reduce = useReducedMotion();
  const sources = [
    { icon: Newspaper, label: "News", x: "8%", y: "12%" },
    { icon: Megaphone, label: "Social", x: "5%", y: "62%" },
    { icon: Users, label: "Stakeholders", x: "82%", y: "10%" },
    { icon: Radio, label: "Broadcast", x: "85%", y: "72%" },
    { icon: Globe2, label: "Web", x: "44%", y: "4%" },
    { icon: Activity, label: "Signals", x: "46%", y: "94%" },
  ];
  return (
    <div className="relative aspect-[5/4] w-full">
      {/* Halo */}
      <div className="absolute inset-[18%] rounded-full bg-gradient-brand opacity-25 blur-3xl" />
      <div className="absolute inset-0">
        {/* SVG lines from sources to center */}
        <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          <defs>
            <linearGradient id="line" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#FF2D78" stopOpacity="0" />
              <stop offset="50%" stopColor="#FF2D78" stopOpacity="0.55" />
              <stop offset="100%" stopColor="#6B5BF5" stopOpacity="0.9" />
            </linearGradient>
          </defs>
          {sources.map((s, i) => {
            const sx = parseFloat(s.x);
            const sy = parseFloat(s.y);
            return (
              <line
                key={i}
                x1={sx}
                y1={sy}
                x2={50}
                y2={50}
                stroke="url(#line)"
                strokeWidth={0.25}
                className={reduce ? "" : "pulse-line"}
                style={{ animationDelay: `${i * 0.4}s` }}
              />
            );
          })}
        </svg>

        {sources.map((s, i) => (
          <motion.div
            key={s.label}
            className="absolute -translate-x-1/2 -translate-y-1/2"
            style={{ left: s.x, top: s.y }}
            initial={reduce ? false : { opacity: 0, scale: 0.7 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 + i * 0.08, duration: 0.5 }}
          >
            <div className="flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 backdrop-blur-md">
              <s.icon className="h-3.5 w-3.5 text-white/90" />
              <span className="text-[11px] font-medium text-white/90">{s.label}</span>
            </div>
          </motion.div>
        ))}

        {/* Center dashboard pill */}
        <motion.div
          className="absolute left-1/2 top-1/2 w-[58%] -translate-x-1/2 -translate-y-1/2"
          initial={reduce ? false : { opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
        >
          <div className="rounded-2xl border border-white/15 bg-white/10 p-4 shadow-glow backdrop-blur-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-accent" />
                <span className="text-[11px] font-semibold uppercase tracking-widest text-white/70">
                  Intelligence
                </span>
              </div>
              <span className="text-[10px] text-white/70">live</span>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2">
              {[78, 42, 91].map((v, i) => (
                <div key={i} className="rounded-lg bg-white/5 p-2">
                  <div className="text-[10px] text-white/70">
                    {["Reach", "Risk", "Sentiment"][i]}
                  </div>
                  <div className="mt-1 text-lg font-semibold text-white">{v}</div>
                  <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-white/10">
                    <div
                      className="h-full rounded-full bg-gradient-brand"
                      style={{ width: `${v}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-center gap-1.5 text-[10px] text-white/70">
              noise <ArrowRight className="h-3 w-3" /> insight{" "}
              <ArrowRight className="h-3 w-3" /> decision
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

/* ============================ SECTION 2: CHALLENGE ============================ */
function Challenge() {
  const noise = [
    "Breaking news",
    "Tweet thread",
    "TikTok clip",
    "Press release",
    "Reddit post",
    "Newsletter",
    "Influencer reel",
    "Forum comment",
    "AI-generated article",
    "Regulator update",
    "Competitor launch",
    "Live broadcast",
  ];
  const insights = [
    { title: "Narrative shift detected", tag: "Reputation" },
    { title: "Early crisis signal", tag: "Risk" },
    { title: "Stakeholder sentiment up", tag: "Perception" },
  ];
  return (
    <SectionShell id="challenge" tone="soft">
      <SectionHeader
        kicker="The Challenge"
        title={
          <>
            Information is everywhere.{" "}
            <span className="text-gradient-brand">Understanding is harder.</span>
          </>
        }
        body="Organisations are surrounded by news, social conversations, influencers, regulators, competitors, AI-generated content and emerging risks. The challenge is no longer access to information - it is understanding what matters."
      />
      <Reveal stagger>
        <div className="grid items-center gap-10 lg:grid-cols-[1.1fr_auto_1fr]">
          {/* Noise */}
          <div>
            <div className="mb-3 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
              <Filter className="h-3.5 w-3.5" /> Raw signal
            </div>
            <div className="relative flex flex-wrap gap-2 rounded-2xl border border-border/70 bg-background p-5 shadow-soft">
              {noise.map((n, i) => (
                <motion.span
                  key={n}
                  initial={{ opacity: 0, y: 6 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.04 }}
                  className="rounded-full border border-border bg-muted px-2.5 py-1 text-[11.5px] text-muted-foreground"
                >
                  {n}
                </motion.span>
              ))}
            </div>
          </div>

          {/* Arrow */}
          <div className="flex items-center justify-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-brand text-white shadow-pill">
              <ArrowRight className="h-5 w-5" />
            </div>
          </div>

          {/* Insights */}
          <div>
            <div className="mb-3 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-accent">
              <Lightbulb className="h-3.5 w-3.5" /> Meaning
            </div>
            <div className="space-y-3">
              {insights.map((it, i) => (
                <motion.div
                  key={it.title}
                  initial={{ opacity: 0, x: 10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 + i * 0.1 }}
                  className="gradient-border rounded-xl bg-background p-4 shadow-soft"
                >
                  <div className="flex items-center justify-between">
                    <div className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                      {it.tag}
                    </div>
                    <CheckCircle2 className="h-4 w-4 text-accent" />
                  </div>
                  <div className="mt-1.5 font-display text-base font-semibold text-foreground">
                    {it.title}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </Reveal>
    </SectionShell>
  );
}

/* ============================ SECTION 3: SHIFT ============================ */
function Shift() {
  const left = ["Mentions", "Clips", "Alerts", "Volumes", "Reports"];
  const right = [
    "Narratives",
    "Reputation Signals",
    "Stakeholder Perception",
    "Emerging Risks",
    "Strategic Decisions",
  ];
  return (
    <SectionShell id="shift" tone="light">
      <SectionHeader
        kicker="The Shift"
        title={
          <>
            Monitoring is no longer enough.{" "}
            <span className="text-gradient-brand">From data to decisions.</span>
          </>
        }
      />
      <div className="grid items-stretch gap-6 lg:grid-cols-[1fr_auto_1fr]">
        <Reveal>
          <div className="h-full rounded-3xl border border-border bg-secondary/40 p-8">
            <div className="mb-6 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
              <Database className="h-4 w-4" /> Traditional Monitoring
            </div>
            <ul className="space-y-3">
              {left.map((l) => (
                <li
                  key={l}
                  className="flex items-center justify-between rounded-xl border border-border/60 bg-background px-4 py-3 text-[15px] text-muted-foreground"
                >
                  {l}
                  <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40" />
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        {/* Bridge */}
        <div className="hidden items-center justify-center lg:flex">
          <div className="relative flex h-full w-px items-center justify-center">
            <div className="absolute inset-y-0 w-px bg-gradient-to-b from-transparent via-accent to-transparent" />
            <div className="relative z-10 rounded-full bg-gradient-brand p-4 text-white shadow-glow">
              <ArrowRight className="h-5 w-5" />
            </div>
          </div>
        </div>

        <Reveal delay={0.1}>
          <div className="h-full rounded-3xl bg-primary p-8 text-white shadow-glow">
            <div className="mb-6 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-white/60">
              <Wand2 className="h-4 w-4 text-accent" /> Media Intelligence
            </div>
            <ul className="space-y-3">
              {right.map((r) => (
                <li
                  key={r}
                  className="flex items-center justify-between rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-[15px] text-white"
                >
                  {r}
                  <CheckCircle2 className="h-4 w-4 text-accent" />
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>
    </SectionShell>
  );
}

/* ============================ SECTION 4: MEET ============================ */
function Meet() {
  const proof = [
    { value: "15+", label: "Years experience", icon: TrendingUp },
    { value: "150K+", label: "Sources monitored", icon: Database },
    { value: "200+", label: "Clients served", icon: Users },
    { value: "SEE", label: "Regional expertise", icon: Map },
    { value: "AI", label: "Powered analysis", icon: Cpu },
    { value: "Human", label: "Led insight", icon: UserCheck },
  ];
  return (
    <SectionShell id="meet" tone="dark">
      <SectionHeader
        invert
        kicker="Meet Perceptica"
        title={
          <>
            Media intelligence{" "}
            <span className="text-gradient-brand">built for Southeast Europe.</span>
          </>
        }
        body="Perceptica combines AI-powered technology, human expertise and regional understanding to help organisations understand how narratives shape reputation and public perception."
      />
      <Reveal stagger>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {proof.map((p, i) => (
            <motion.div
              key={p.label}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06 }}
              className="group rounded-2xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:bg-white/[0.08]"
            >
              <div className="flex items-center justify-between">
                <div className="text-gradient-brand font-display text-4xl font-medium leading-none">
                  {p.value}
                </div>
                <div className="rounded-lg bg-white/10 p-2 text-white/80">
                  <p.icon className="h-4 w-4" />
                </div>
              </div>
              <div className="mt-4 text-sm text-white/70">{p.label}</div>
            </motion.div>
          ))}
        </div>
      </Reveal>
    </SectionShell>
  );
}

/* ============================ SECTION 5: WHAT WE DO ============================ */
function WhatWeDo() {
  const items = [
    {
      icon: Radio,
      title: "Media Monitoring",
      body: "Track relevant mentions across press, broadcast, online news and social.",
    },
    {
      icon: BarChart3,
      title: "Media Analysis",
      body: "Understand sentiment, visibility, topics and communication impact.",
    },
    {
      icon: Eye,
      title: "Reputation Intelligence",
      body: "Monitor perception and reputation over time across audiences.",
    },
    {
      icon: ShieldAlert,
      title: "Risk & Narrative Intelligence",
      body: "Identify emerging risks and evolving narratives before they escalate.",
    },
  ];
  return (
    <SectionShell id="what" tone="light">
      <SectionHeader kicker="What We Do" title="Four capabilities, one intelligence layer." />
      <Reveal stagger>
        <div className="grid gap-5 md:grid-cols-2">
          {items.map((it, i) => (
            <motion.div
              key={it.title}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="group relative overflow-hidden rounded-3xl border border-border/70 bg-background p-8 shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-card"
            >
              <div className="absolute -right-12 -top-12 h-44 w-44 rounded-full bg-gradient-brand opacity-[0.06] blur-2xl transition-opacity group-hover:opacity-[0.14]" />
              <div className="relative">
                <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-brand text-white shadow-pill">
                  <it.icon className="h-5 w-5" />
                </div>
                <h3 className="font-display text-xl font-semibold">{it.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">
                  {it.body}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </Reveal>
    </SectionShell>
  );
}

/* ============================ SECTION 6: FLOW ============================ */
function Flow() {
  const stages = [
    { icon: Database, title: "Collect", body: "Monitor media and public conversations." },
    { icon: Layers, title: "Structure", body: "Organise and enrich information." },
    { icon: Activity, title: "Analyse", body: "Detect sentiment, topics, narratives and risks." },
    { icon: Lightbulb, title: "Explain", body: "Transform signals into meaningful insights." },
    { icon: Target, title: "Act", body: "Support better communication and business decisions." },
  ];
  return (
    <SectionShell id="flow" tone="soft">
      <SectionHeader
        kicker="Intelligence Flow"
        title={<>How intelligence <span className="text-gradient-brand">is created.</span></>}
      />
      <Reveal stagger>
        <div className="relative">
          {/* Desktop horizontal flow */}
          <div className="hidden lg:grid lg:grid-cols-9 lg:items-stretch lg:gap-0">
            {stages.map((s, i) => (
              <Fragment key={s.title}>
                <motion.div
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="relative col-span-1 flex flex-col items-center text-center"
                  style={{ gridColumn: `span ${i === stages.length - 1 ? 1 : 1}` }}
                >
                  <div className="mb-3 flex h-16 w-16 items-center justify-center rounded-2xl border border-border bg-background shadow-soft">
                    <s.icon className="h-6 w-6 text-accent" />
                  </div>
                  <div className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                    Stage {i + 1}
                  </div>
                  <div className="mt-1 font-display text-lg font-semibold text-foreground">
                    {s.title}
                  </div>
                  <p className="mt-1 max-w-[180px] text-[13px] leading-relaxed text-muted-foreground">
                    {s.body}
                  </p>
                </motion.div>
                {i < stages.length - 1 && (
                  <div className="col-span-1 flex items-center justify-center pt-7">
                    <div className="h-px w-full bg-gradient-to-r from-transparent via-accent/40 to-transparent" />
                  </div>
                )}
              </Fragment>
            ))}
          </div>

          {/* Mobile vertical flow */}
          <div className="space-y-6 lg:hidden">
            {stages.map((s, i) => (
              <div key={s.title} className="flex items-start gap-4">
                <div className="flex flex-col items-center">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-border bg-background shadow-soft">
                    <s.icon className="h-5 w-5 text-accent" />
                  </div>
                  {i < stages.length - 1 && (
                    <div className="my-2 h-10 w-px bg-gradient-to-b from-accent/40 to-transparent" />
                  )}
                </div>
                <div className="pt-2">
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                    Stage {i + 1}
                  </div>
                  <div className="mt-0.5 font-display text-base font-semibold">{s.title}</div>
                  <p className="mt-1 text-[13.5px] leading-relaxed text-muted-foreground">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Reveal>
    </SectionShell>
  );
}

/* ============================ SECTION 7: PLATFORM ============================ */
function Platform() {
  const features = [
    { icon: Activity, title: "Real-Time Monitoring" },
    { icon: LayoutDashboard, title: "Custom Dashboards" },
    { icon: Bell, title: "Alerts & Newsletters" },
    { icon: BarChart3, title: "Sentiment Analysis" },
    { icon: Tag, title: "Topic Analysis" },
    { icon: Search, title: "Entity Recognition" },
    { icon: Filter, title: "Source Filtering" },
    { icon: Wand2, title: "AI Summaries" },
    { icon: FileText, title: "Report Exports" },
  ];
  return (
    <SectionShell id="platform" tone="dark">
      <SectionHeader
        invert
        kicker="The Platform"
        title={
          <>
            One platform for monitoring,{" "}
            <span className="text-gradient-brand">analysis and insight delivery.</span>
          </>
        }
      />
      <Reveal>
        <div className="relative mx-auto max-w-5xl">
          {/* Mock dashboard */}
          <div className="relative overflow-hidden rounded-3xl border border-white/15 bg-white/[0.04] p-2 shadow-glow backdrop-blur-xl">
            <div className="rounded-2xl border border-white/10 bg-primary p-5">
              <div className="flex items-center justify-between border-b border-white/10 pb-3">
                <div className="flex items-center gap-2">
                  <div className="h-2.5 w-2.5 rounded-full bg-accent" />
                  <div className="h-2.5 w-2.5 rounded-full bg-white/30" />
                  <div className="h-2.5 w-2.5 rounded-full bg-white/20" />
                  <span className="ml-3 text-[12px] font-medium text-white/70">
                    Perceptica · Dashboard
                  </span>
                </div>
                <span className="text-[10px] text-white/70">Live · Last 24h</span>
              </div>
              <div className="mt-4 grid gap-3 md:grid-cols-3">
                {[
                  { l: "Mentions", v: "12,847", d: "+18%" },
                  { l: "Reach", v: "4.2M", d: "+9%" },
                  { l: "Sentiment", v: "+0.41", d: "stable" },
                ].map((s) => (
                  <div key={s.l} className="rounded-xl border border-white/10 bg-white/[0.04] p-4">
                    <div className="text-[11px] uppercase tracking-widest text-white/70">{s.l}</div>
                    <div className="mt-1 font-display text-2xl font-semibold text-white">{s.v}</div>
                    <div className="mt-1 text-[11px] text-accent">{s.d}</div>
                  </div>
                ))}
              </div>
              <div className="mt-3 rounded-xl border border-white/10 bg-white/[0.04] p-4">
                <div className="mb-3 flex items-center justify-between text-[11px] uppercase tracking-widest text-white/70">
                  <span>Narrative trends</span>
                  <span>7d</span>
                </div>
                <div className="flex h-24 items-end gap-1.5">
                  {Array.from({ length: 28 }).map((_, i) => {
                    const h = 24 + Math.round(40 + Math.sin(i / 2) * 30 + (i % 5) * 4);
                    return (
                      <div
                        key={i}
                        className="flex-1 rounded-sm bg-gradient-to-t from-[#6B5BF5] to-[#FF2D78] opacity-80"
                        style={{ height: `${Math.min(h, 100)}%` }}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Floating feature chips */}
          <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.04 }}
                className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 backdrop-blur-sm transition-all hover:border-white/20 hover:bg-white/[0.08]"
              >
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-brand text-white">
                  <f.icon className="h-4 w-4" />
                </div>
                <span className="text-sm font-medium text-white">{f.title}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </Reveal>
    </SectionShell>
  );
}

/* ============================ SECTION 8: AI + HUMAN ============================ */
function AiHuman() {
  const parts = [
    { icon: Cpu, title: "AI Models", body: "Detect patterns at scale." },
    { icon: UserCheck, title: "Media Analysts", body: "Validate context and meaning." },
    { icon: Map, title: "Regional Experts", body: "Read local nuance and dynamics." },
  ];
  return (
    <SectionShell id="ai-human" tone="light">
      <SectionHeader
        kicker="AI + Human Expertise"
        title={
          <>
            Technology finds patterns.{" "}
            <span className="text-gradient-brand">Experts explain meaning.</span>
          </>
        }
      />
      <Reveal>
        <div className="grid items-center gap-6 lg:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1.1fr]">
          {parts.map((p, i) => (
            <Fragment key={p.title}>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="rounded-2xl border border-border bg-background p-6 text-center shadow-soft"
              >
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-brand text-white shadow-pill">
                  <p.icon className="h-5 w-5" />
                </div>
                <div className="font-display text-lg font-semibold">{p.title}</div>
                <p className="mt-1 text-[13px] text-muted-foreground">{p.body}</p>
              </motion.div>
              <div className="hidden items-center justify-center text-2xl font-light text-muted-foreground lg:flex">
                {i < parts.length - 1 ? "+" : "="}
              </div>
            </Fragment>
          ))}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="rounded-2xl bg-primary p-6 text-center text-white shadow-glow"
          >
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-brand text-white shadow-pill">
              <Wand2 className="h-5 w-5" />
            </div>
            <div className="font-display text-lg font-semibold">Actionable Intelligence</div>
            <p className="mt-1 text-[13px] text-white/70">Decisions you can defend.</p>
          </motion.div>
        </div>
      </Reveal>
    </SectionShell>
  );
}

/* ============================ SECTION 9: SEE ============================ */
function SEE() {
  const countries = [
    "Bulgaria",
    "Romania",
    "Greece",
    "Serbia",
    "North Macedonia",
    "Albania",
    "Croatia",
    "Slovenia",
    "Bosnia",
    "Montenegro",
    "Kosovo",
  ];
  const highlights = [
    { icon: Newspaper, label: "Local media" },
    { icon: Globe2, label: "Local languages" },
    { icon: UserCheck, label: "Regional expertise" },
    { icon: Network, label: "Cross-border narratives" },
  ];
  return (
    <SectionShell id="see" tone="soft">
      <div className="grid items-center gap-12 lg:grid-cols-2">
        <Reveal>
          <Kicker>Southeast Europe Advantage</Kicker>
          <h2 className="mt-5 font-display text-[clamp(1.8rem,3.5vw,2.75rem)] font-medium leading-[1.05] tracking-[-0.025em]">
            Local context is the difference between{" "}
            <span className="text-gradient-brand">data and intelligence.</span>
          </h2>
          <p className="mt-5 text-[1.05rem] leading-relaxed text-muted-foreground">
            Global tools often miss regional nuance. Perceptica is built around local
            sources, local languages and local media dynamics across Southeast Europe.
          </p>
          <div className="mt-7 grid gap-3 sm:grid-cols-2">
            {highlights.map((h) => (
              <div
                key={h.label}
                className="flex items-center gap-3 rounded-xl border border-border bg-background px-4 py-3 shadow-soft"
              >
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-brand text-white">
                  <h.icon className="h-4 w-4" />
                </div>
                <span className="text-sm font-medium">{h.label}</span>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="relative aspect-square overflow-hidden rounded-3xl bg-primary p-6 shadow-glow">
            <div className="pointer-events-none absolute inset-0 bg-radial-hero opacity-80" />
            <div className="grid-overlay" />
            <div className="relative flex h-full flex-col">
              <div className="flex items-center justify-between text-white">
                <div className="text-[11px] font-semibold uppercase tracking-widest text-white/60">
                  Regional Coverage Map
                </div>
                <span className="text-[10px] text-white/70">11 markets</span>
              </div>

              {/* Stylised region grid */}
              <div className="relative mt-6 grid flex-1 grid-cols-4 gap-2">
                {countries.map((c, i) => (
                  <motion.div
                    key={c}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.05 }}
                    className="flex items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] p-2 text-center"
                  >
                    <div>
                      <div className="mx-auto mb-1 h-2 w-2 rounded-full bg-accent" />
                      <div className="text-[10.5px] font-medium text-white/85">{c}</div>
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="mt-4 flex items-center justify-between text-[11px] text-white/70">
                <span>20+ languages</span>
                <span>Native analysts</span>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </SectionShell>
  );
}

/* ============================ SECTION 10: USE CASES ============================ */
function UseCases() {
  const items = [
    { icon: Megaphone, title: "Corporate Communications", body: "Measure communication impact and reputation." },
    { icon: AlertTriangle, title: "Crisis Management", body: "Detect early warning signals." },
    { icon: Target, title: "Marketing & Brand Teams", body: "Track visibility and perception." },
    { icon: Landmark, title: "Public Affairs", body: "Monitor policy and stakeholder narratives." },
    { icon: Briefcase, title: "Employer Branding", body: "Understand employer reputation." },
    { icon: BarChart3, title: "Competitive Intelligence", body: "Compare positioning against competitors." },
  ];
  return (
    <SectionShell id="use-cases" tone="light">
      <SectionHeader kicker="Use Cases" title="Who turns Perceptica into a decision-making advantage." />
      <Reveal stagger>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((it, i) => (
            <motion.div
              key={it.title}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06 }}
            >
              <FeatureCard icon={it.icon} title={it.title} body={it.body} />
            </motion.div>
          ))}
        </div>
      </Reveal>
    </SectionShell>
  );
}

/* ============================ SECTION 11: INDUSTRIES ============================ */
function Industries() {
  const inds = [
    { icon: Landmark, label: "Financial Services" },
    { icon: Pill, label: "Pharma" },
    { icon: ShoppingBag, label: "FMCG" },
    { icon: Store, label: "Retail" },
    { icon: Car, label: "Automotive" },
    { icon: Building2, label: "Public Sector" },
    { icon: HeartHandshake, label: "NGOs & Institutions" },
  ];
  return (
    <SectionShell id="industries" tone="soft">
      <SectionHeader kicker="Industries" title="Trusted across regulated and reputation-sensitive sectors." />
      <Reveal stagger>
        <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {inds.map((it, i) => (
            <motion.div
              key={it.label}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="group flex flex-col items-start rounded-2xl border border-border/70 bg-background p-6 shadow-soft transition-all hover:-translate-y-1 hover:shadow-card"
            >
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-brand-soft text-accent transition-all group-hover:bg-gradient-brand group-hover:text-white">
                <it.icon className="h-5 w-5" />
              </div>
              <div className="font-display text-base font-semibold">{it.label}</div>
            </motion.div>
          ))}
        </div>
      </Reveal>
    </SectionShell>
  );
}

/* ============================ SECTION 12: DELIVERY ============================ */
function Delivery() {
  const formats = [
    { icon: LayoutDashboard, label: "Dashboard", note: "Live operational view" },
    { icon: Bell, label: "Alert", note: "Instant signal" },
    { icon: Mail, label: "Newsletter", note: "Daily digest" },
    { icon: FileText, label: "Executive Summary", note: "Decision brief" },
    { icon: Newspaper, label: "PDF Report", note: "Deep analysis" },
    { icon: Code2, label: "API Export", note: "System integration" },
  ];
  return (
    <SectionShell id="delivery" tone="light">
      <SectionHeader
        kicker="Insight Delivery"
        title={
          <>
            From dashboards <span className="text-gradient-brand">to decisions.</span>
          </>
        }
        body="Insight travels through the organisation in the format each team needs."
      />
      <Reveal stagger>
        <div className="relative">
          {/* Central spine */}
          <div className="pointer-events-none absolute left-1/2 top-0 hidden h-full w-px -translate-x-1/2 bg-gradient-to-b from-transparent via-accent/40 to-transparent lg:block" />
          <div className="grid gap-5 lg:grid-cols-2">
            {formats.map((f, i) => (
              <motion.div
                key={f.label}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.07 }}
                className={`group flex items-center gap-5 rounded-2xl border border-border/70 bg-background p-5 shadow-soft transition-all hover:-translate-y-0.5 hover:shadow-card ${
                  i % 2 === 0 ? "lg:mr-8" : "lg:ml-8"
                }`}
              >
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-brand text-white shadow-pill">
                  <f.icon className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <div className="font-display text-base font-semibold">{f.label}</div>
                  <div className="text-[13px] text-muted-foreground">{f.note}</div>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-accent" />
              </motion.div>
            ))}
          </div>
        </div>
      </Reveal>
    </SectionShell>
  );
}

/* ============================ SECTION 13: WHY ============================ */
function Why() {
  const cards = [
    { icon: Map, title: "Regional Intelligence", body: "Built natively for Southeast Europe." },
    { icon: Cpu, title: "AI-Powered Technology", body: "Scalable detection and enrichment." },
    { icon: UserCheck, title: "Human Analysis", body: "Senior analysts validate every story." },
    { icon: FileText, title: "Custom Reporting", body: "Reports shaped around your decisions." },
    { icon: HeartHandshake, title: "Trusted Partner", body: "Long-term relationships, not transactions." },
  ];
  return (
    <SectionShell id="why" tone="dark">
      <SectionHeader invert kicker="Why Perceptica" title="Five reasons teams choose us." />
      <Reveal stagger>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          {cards.map((c, i) => (
            <motion.div
              key={c.title}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06 }}
              className="group rounded-2xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-white/20 hover:bg-white/[0.08]"
            >
              <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-brand text-white shadow-pill">
                <c.icon className="h-5 w-5" />
              </div>
              <div className="font-display text-base font-semibold text-white">{c.title}</div>
              <p className="mt-2 text-[13.5px] leading-relaxed text-white/70">{c.body}</p>
            </motion.div>
          ))}
        </div>
      </Reveal>
    </SectionShell>
  );
}

/* ============================ SECTION 14: TRUSTED ============================ */
function Trusted() {
  return (
    <SectionShell id="trusted" tone="light">
      <SectionHeader kicker="Trusted By" title="Trusted by leading organisations." />
      <Reveal>
        <div className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-border/70 bg-border/70 sm:grid-cols-3 lg:grid-cols-5">
          {Array.from({ length: 15 }).map((_, i) => (
            <div
              key={i}
              className="flex h-24 items-center justify-center bg-background transition-colors hover:bg-secondary/40"
            >
              <div className="h-6 w-24 rounded-md bg-muted" />
            </div>
          ))}
        </div>
        <p className="mt-6 text-center text-[13px] text-muted-foreground">
          Client logo wall - placeholders ready for partner artwork.
        </p>
      </Reveal>
    </SectionShell>
  );
}

/* ============================ SECTION 15: FINAL CTA ============================ */
function FinalCta() {
  return (
    <section
      id="cta"
      className="relative overflow-hidden bg-primary py-28 text-white lg:py-36"
    >
      <div className="pointer-events-none absolute inset-0 bg-radial-hero opacity-90" />
      <div className="grid-overlay" />
      <div className="relative mx-auto max-w-3xl px-6 text-center lg:px-10">
        <Reveal>
          <Kicker>Get Started</Kicker>
          <h2 className="mt-6 font-display text-[clamp(2.25rem,5vw,4rem)] font-medium leading-[1.02] tracking-[-0.03em]">
            Understand what{" "}
            <span className="text-gradient-brand">shapes your reputation.</span>
          </h2>
          <p className="mt-6 text-[1.1rem] text-white/75">
            Move from monitoring to intelligence.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <Button size="lg" asChild>
              <LocaleLink to="/request-a-demo">
                Request a Demo <ArrowRight className="h-4 w-4" />
              </LocaleLink>
            </Button>
            <Button size="lg" variant="outlineLight" asChild>
              <LocaleLink to="/contact-us">Contact Us</LocaleLink>
            </Button>
          </div>
          <div className="mt-14 flex flex-wrap items-center justify-center gap-x-8 gap-y-2 border-t border-white/10 pt-8 text-[13.5px] text-white/65">
            <a href="https://www.perceptica.com" className="hover:text-white">www.perceptica.com</a>
            <a href="mailto:sales@perceptica.com" className="hover:text-white">sales@perceptica.com</a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
